import { useState, useCallback } from 'react';

interface ToastState {
  isOpen: boolean;
  title: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  onConfirm?: () => void;
  showCancel?: boolean;
  confirmText?: string;
  cancelText?: string;
}

interface UseNominationToastReturn {
  toastState: ToastState;
  showToast: (config: Omit<ToastState, 'isOpen'>) => void;
  showSuccess: (title: string, message: string, onConfirm?: () => void) => void;
  showError: (title: string, message: string, onConfirm?: () => void) => void;
  showWarning: (title: string, message: string, onConfirm?: () => void) => void;
  showInfo: (title: string, message: string, onConfirm?: () => void) => void;
  closeToast: () => void;
}

/**
 * Custom hook for nomination-specific toast notifications
 * Separate from the main dialog system to avoid conflicts with self-appraisal
 */
export const useNominationToast = (): UseNominationToastReturn => {
  const [toastState, setToastState] = useState<ToastState>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    onConfirm: undefined,
    showCancel: false,
    confirmText: 'OK',
    cancelText: 'Cancel',
  });

  const showToast = useCallback((config: Omit<ToastState, 'isOpen'>) => {
    setToastState({
      ...config,
      isOpen: true,
    });
  }, []);

  const closeToast = useCallback(() => {
    setToastState(prev => ({
      ...prev,
      isOpen: false,
    }));
  }, []);

  const showSuccess = useCallback((title: string, message: string, onConfirm?: () => void) => {
    showToast({
      title,
      message,
      type: 'success',
      onConfirm,
      showCancel: false,
      confirmText: 'OK',
    });
  }, [showToast]);

  const showError = useCallback((title: string, message: string, onConfirm?: () => void) => {
    showToast({
      title,
      message,
      type: 'error',
      onConfirm,
      showCancel: false,
      confirmText: 'OK',
    });
  }, [showToast]);

  const showWarning = useCallback((title: string, message: string, onConfirm?: () => void) => {
    showToast({
      title,
      message,
      type: 'warning',
      onConfirm,
      showCancel: false,
      confirmText: 'OK',
    });
  }, [showToast]);

  const showInfo = useCallback((title: string, message: string, onConfirm?: () => void) => {
    showToast({
      title,
      message,
      type: 'info',
      onConfirm,
      showCancel: false,
      confirmText: 'OK',
    });
  }, [showToast]);

  return {
    toastState,
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    closeToast,
  };
};
